import { ReactChild, ReactNode, FC } from 'react'
import { css } from '@emotion/react'
import { useFragment } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import { mq, WindowWidth } from '@lifedot/styles/mediaQuery'
import { palette } from '@lifedot/styles/palette'
import { typography } from '@lifedot/styles/typography'
import { listPath } from '@lifedot/router'
import { OverviewWrapper_cemetery$key } from './__generated__/OverviewWrapper_cemetery.graphql'
import { CallToAction } from './CallToAction'
import { useIsUrban } from '@lifedot/hooks/use-is-urban'
import { PrefectureRomas } from '@lifedot/constants/prefectures'

const styles = {
  header: css({
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: 20,
    alignItems: 'start',
    backgroundColor: palette.white,
    padding: '32px 40px',
    [mq('max', WindowWidth.TABLET)]: {
      gridTemplateColumns: '3fr 2fr'
    },
    [mq('sp')]: {
      gridTemplateColumns: '1fr',
      padding: '12px 8px'
    }
  }),
  access: css({
    marginTop: 4
  }),
  content: css({
    marginTop: 20
  })
}

interface Props {
  cemetery: OverviewWrapper_cemetery$key
  children: ReactNode
  title: ReactChild
}

export const OverviewWrapper: FC<Props> = ({ cemetery, children, title }) => {
  const {
    cemeteryId,
    nearest_station: nearestStation,
    prefecture,
    city,
    requestable,
    section_types: sectionTypes
  } = useFragment(fragment, cemetery)

  const isUrban = useIsUrban(prefecture.roma as PrefectureRomas)

  return (
    <header css={styles.header}>
      <div>
        <h1 css={typography.headingM}>{title}</h1>
        <p css={[typography.textM, styles.access]}>
          <a
            href={listPath({
              prefectureRoma: prefecture.roma,
              cityId: city.cityId
            })}
          >
            {prefecture.name}
            {city.name}
          </a>
          {nearestStation.name ? (
            <>
              <span> / </span>
              <a
                href={listPath({
                  prefectureRoma: prefecture.roma,
                  stationId: nearestStation.stationId
                })}
              >
                {nearestStation.name}駅
              </a>
            </>
          ) : (
            ''
          )}
        </p>
        <div css={styles.content}>{children}</div>
      </div>
      <CallToAction
        cemeteryId={cemeteryId}
        requestable={requestable}
        sectionTypes={[...sectionTypes]}
        hideTelButton={!isUrban || !requestable}
      />
    </header>
  )
}

const fragment = graphql`
  fragment OverviewWrapper_cemetery on Cemetery {
    cemeteryId: id
    nearest_station {
      stationId: id
      name
    }
    prefecture {
      roma
      name
    }
    city {
      cityId: id
      name
    }
    requestable
    section_types {
      name
      code
    }
  }
`
